import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { PrivacyMode, ProjectFull } from "@backstory/protocol";
import { api, ingestUrl, queryKeys, type BrowserKey, type NewBrowserKey, type ProjectInstall } from "../../api";
import { ErrorState } from "../../components/States";
import { OrgLayout } from "../../components/Shell";
import { BusyButton, CopyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { useCan } from "../../auth/roles";
import { LimitsBanner, recordingInstruction, useOrgLimits } from "../../features/billing/limits";
import { OrgBackendKeys } from "../../features/backend/ServerKeys";

const PLAN_DAYS: Record<string, number> = { free: 7, team: 30, business: 90, enterprise: 400 };

/** Choices a plan may offer. Every plan starts at 7 days; anything above the plan's cap is left out. */
const RETENTION_LADDER = [7, 14, 30, 60, 90, 180, 365, 400];

function retentionOptions(max: number, current: number): number[] {
  const options = RETENTION_LADDER.filter((days) => days <= max);
  if (Number.isInteger(current) && current >= 1 && !options.includes(current)) options.push(current);
  return options.sort((a, b) => a - b);
}

function retentionLabel(days: number): string {
  if (days === 1) return "1 day";
  if (days === 365) return "1 year";
  return `${days} days`;
}

type RecordMode = ProjectFull["recording"]["mode"];

interface Draft {
  mode: RecordMode;
  sample: string;
  privacy: PrivacyMode;
  days: string;
  sites: string[];
  eventCap: string;
}

function sitesFrom(origins: string[] | undefined): string[] {
  return (origins ?? []).filter((s) => s && s !== "*");
}

function sameSites(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((site, i) => site === b[i]);
}

function draftOf(k: ProjectFull): Draft {
  return {
    mode: k.recording?.mode || "always",
    sample: String(Math.round((k.recording?.baselineSampleRate ?? 1) * 100)),
    privacy: k.privacyMode,
    days: String(k.retentionDays || 7),
    sites: sitesFrom(k.allowedOrigins),
    eventCap: k.customEventsCap ? String(k.customEventsCap) : "",
  };
}

/** A pasted address keeps its scheme and host. A trailing slash is not part of the site. */
function normalizeSite(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

const SITE_PATTERN = /^https?:\/\/[^/\s]+$/i;

const FRAMEWORKS = [
  { id: "html", label: "HTML" },
  { id: "javascript", label: "JavaScript" },
  { id: "react", label: "React" },
  { id: "next", label: "Next.js" },
  { id: "vue", label: "Vue" },
  { id: "angular", label: "Angular" },
  { id: "svelte", label: "Svelte" },
] as const;

/** Shown in place of a key that cannot be read back. */
const KEY_PLACEHOLDER = "your-browser-key";

function installSnippet(framework: string, key: string): string {
  const init = `Backstory.init({\n  projectKey: "${key}",\n  ingestUrl: "${ingestUrl()}",\n});`;
  switch (framework) {
    case "html":
      return `npm install @backstory/browser\n\nimport { Backstory } from "@backstory/browser";\n\n${init}`;
    case "react":
      return `import { Backstory } from "@backstory/browser";\nimport { createRoot } from "react-dom/client";\n\n${init}\ncreateRoot(document.getElementById("root")!).render(<App />);`;
    case "next":
      return `"use client";\nimport { useEffect } from "react";\nimport { Backstory } from "@backstory/browser";\n\nexport function BackstoryProvider() {\n  useEffect(() => { ${init.replaceAll("\n", " ")} }, []);\n  return null;\n}`;
    case "vue":
      return `import { Backstory } from "@backstory/browser";\n\nexport default defineNuxtPlugin(() => {\n  ${init.replaceAll("\n", "\n  ")}\n});`;
    case "angular":
      return `import { Backstory } from "@backstory/browser";\n\n${init}\nbootstrapApplication(AppComponent, appConfig);`;
    case "svelte":
      return `// src/hooks.client.ts\nimport { Backstory } from "@backstory/browser";\n\n${init}`;
    default:
      return `import { Backstory } from "@backstory/browser";\n\n${init}`;
  }
}

function checkText(check: NonNullable<ProjectInstall["check"]>): string {
  switch (check) {
    case "recording_off":
      return "Recording is turned off for this key, so a visit is not saved. Choose Every visit while you test.";
    case "triggers_only":
      return "This key only saves a visit when something goes wrong. Cause an error on the page, or record every visit while you test.";
    case "sample_none":
      return "The share of visits to record is zero, so a normal visit is not saved. Set it above zero, or record every visit while you test.";
    case "empty_visit":
      return "A visit started, but nothing on the page was saved. Keep the page open for a few seconds and try the main action again.";
  }
}

function noticeText(notice: NonNullable<ProjectInstall["notice"]>): string {
  if (notice.code === "origin_not_allowed") {
    return notice.origin
      ? `A page at ${notice.origin} was blocked. Add that site, or leave the list empty.`
      : "A page was blocked because it is not one of the sites above.";
  }
  if (notice.code === "unauthorized") return "The key on that page was not recognized.";
  return "A visit was refused.";
}

/** A refusal newer than the last visit is the thing to show. An older visit must not hide it. */
function refusalIsCurrent(data: ProjectInstall | undefined): boolean {
  const notice = data?.notice;
  if (!notice) return false;
  if (!data?.session) return true;
  return new Date(notice.at).getTime() > new Date(data.session.startedAt).getTime();
}

function sameDraft(a: Draft, b: Draft): boolean {
  return a.mode === b.mode && a.sample === b.sample && a.privacy === b.privacy && a.days === b.days && a.eventCap === b.eventCap && sameSites(a.sites, b.sites);
}

/** One site at a time. The list is what gets saved; text still in the field is not a site yet. */
export function SiteList({
  sites,
  onChange,
  disabled,
  onPending,
}: {
  sites: string[];
  onChange: (sites: string[]) => void;
  disabled?: boolean;
  onPending?: (pending: boolean) => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | undefined>();
  const pending = normalizeSite(value).length > 0;

  useEffect(() => {
    onPending?.(pending);
  }, [pending, onPending]);
  useEffect(() => () => onPending?.(false), [onPending]);

  const add = () => {
    const site = normalizeSite(value);
    if (!site) return;
    if (!SITE_PATTERN.test(site)) {
      setError("Enter a site like https://app.example.com.");
      return;
    }
    if (sites.some((existing) => existing.toLowerCase() === site.toLowerCase())) {
      setError("That site is already listed.");
      return;
    }
    onChange([...sites, site]);
    setValue("");
    setError(undefined);
  };

  return (
    <div className="site-list">
      <Field label="Sites that may send visits" hint="Like https://app.example.com. Leave this empty to accept every site." error={error}>
        {(props) => (
          <>
            <div className="site-list-add">
              <input
                {...props}
                className="input"
                placeholder="https://app.example.com"
                value={value}
                disabled={disabled}
                onChange={(event) => {
                  setValue(event.target.value);
                  setError(undefined);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    add();
                  }
                }}
              />
              <button type="button" className="btn" disabled={disabled || !pending} onClick={add}>Add site</button>
            </div>
            {sites.length > 0 && (
              <ul className="site-list-rows">
                {sites.map((site) => (
                  <li key={site}>
                    <span>{site}</span>
                    {!disabled && (
                      <button type="button" className="btn ghost sm" aria-label={`Remove ${site}`} onClick={() => onChange(sites.filter((existing) => existing !== site))}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Field>
    </div>
  );
}

/**
 * Each key is one app. Recording, privacy, how long sessions are kept, and
 * which sites may send data are edited here. A page that is already open
 * picks the saved values up the next time it loads.
 */
export function SdkKeysPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useQuery({ queryKey: queryKeys.projects(orgId), queryFn: () => api.orgs.projects(orgId) });
  const browserKeys = useQuery({ queryKey: queryKeys.browserKeys(orgId), queryFn: () => api.orgs.browserKeys(orgId) });
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyApp, setKeyApp] = useState("");
  const [keyName, setKeyName] = useState("");
  const [keyErrors, setKeyErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<NewBrowserKey | null>(null);
  /** The key just made, kept only while its install steps are open. */
  const [installSecret, setInstallSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<BrowserKey | null>(null);
  const [setup, setSetup] = useState<ProjectFull | null>(null);
  const [framework, setFramework] = useState<string>("html");
  const [siteText, setSiteText] = useState<string[]>([]);
  const [siteSaved, setSiteSaved] = useState<string[]>([]);
  const [setupSitePending, setSetupSitePending] = useState(false);
  const [draftSitePending, setDraftSitePending] = useState(false);

  const can = useCan();
  // The same limits the recorder receives. The banner and each app's badge
  // read this one answer, so a cap that narrows recording shows in both.
  const limits = useOrgLimits(orgId);
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const keepDays = PLAN_DAYS[org.data?.plan ?? ""] ?? 7;
  const [editing, setEditing] = useState<ProjectFull | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [savedDraft, setSavedDraft] = useState<Draft | null>(null);
  const [discard, setDiscard] = useState(false);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.projects(orgId) });
    void qc.invalidateQueries({ queryKey: queryKeys.projectPicker(orgId) });
    void qc.invalidateQueries({ queryKey: queryKeys.browserKeys(orgId) });
  };

  const openInstall = (k: ProjectFull, secret?: string) => {
    const sites = sitesFrom(k.allowedOrigins);
    setSetup(k);
    setInstallSecret(secret ?? null);
    setSiteText(sites);
    setSiteSaved(sites);
    setFramework("html");
  };
  const createKey = useMutation({
    mutationFn: () => api.projects.createBrowserKey(chosenApp, keyName.trim()),
    onSuccess: (made) => {
      setKeyOpen(false);
      setKeyName("");
      setKeyErrors({});
      setCreated(made);
      invalidate();
    },
    onError: (e) => {
      const err = e as Error & { field?: string; body?: { field?: string } };
      const field = err.field ?? err.body?.field;
      if (field) setKeyErrors({ [field]: err.message });
      else toast.push("error", err.message);
    },
  });
  const openRecording = (k: ProjectFull) => {
    const next = draftOf(k);
    setEditing(k);
    setDraft(next);
    setSavedDraft(next);
  };
  const dirty = !!draft && !!savedDraft && !sameDraft(draft, savedDraft);
  const sample = Number(draft?.sample);
  const days = Number(draft?.days);
  const eventCap = draft?.eventCap.trim() ?? "";
  const eventCapError = eventCap !== "" && (!/^\d+$/.test(eventCap) || Number(eventCap) < 1) ? "Enter a whole number, or leave it blank." : undefined;
  const sampleError = draft?.mode === "conditional" && (draft.sample.trim() === "" || !Number.isInteger(sample) || sample < 0 || sample > 100) ? "Enter a whole number from 0 to 100." : undefined;
  const askClose = () => {
    if (dirty && can.admin) setDiscard(true);
    else setEditing(null);
  };
  const saveRecording = useMutation({
    mutationFn: () => {
      if (!editing || !draft) throw new Error("Nothing to save");
      return api.projects.patch(editing.id, {
        privacyMode: draft.privacy,
        ...(days <= keepDays ? { retentionDays: days, eventsRetentionDays: days } : {}),
        allowedOrigins: draft.sites,
        customEventsCap: eventCap === "" ? 0 : Number(eventCap),
        recording: {
          ...editing.recording,
          mode: draft.mode,
          baselineSampleRate: sample / 100,
        },
      });
    },
    onSuccess: (updated) => {
      const next = draftOf(updated);
      setEditing(updated);
      setDraft(next);
      setSavedDraft(next);
      invalidate();
      toast.push("success", "Settings saved. A page that is already open picks this up the next time it loads.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const siteDirty = !sameSites(siteText, siteSaved);
  const watch = useQuery({
    queryKey: ["project-install", setup?.id, installSecret],
    enabled: !!setup,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.session || refusalIsCurrent(data) || data.check === "empty_visit") return 3000;
      if (data.signal) return false;
      const age = Date.now() - new Date(data.session.startedAt).getTime();
      return age < 3 * 60_000 ? 3000 : false;
    },
    queryFn: async (): Promise<ProjectInstall & { keyOk: boolean }> => {
      const project = setup;
      if (!project) return { keyOk: true };
      let keyOk = true;
      if (installSecret) {
        try {
          const res = await fetch(`${ingestUrl().replace(/\/v1\/ingest\/?$/, "")}/v1/config?key=${encodeURIComponent(installSecret)}`, { cache: "no-store" });
          if (res.status === 404) keyOk = false;
        } catch {
          keyOk = true;
        }
      }
      try {
        const status = await api.projects.install(project.id);
        return { ...status, keyOk };
      } catch {
        const page = await api.sessions({ limit: 1, projectId: project.id });
        const item = page.items.find((session) => session.projectId === project.id);
        return { session: item ? { id: item.id, url: item.url, title: item.title, startedAt: item.startedAt } : undefined, keyOk };
      }
    },
  });
  const saveSites = useMutation({
    mutationFn: () => {
      if (!setup) throw new Error("Nothing to save");
      return api.projects.patch(setup.id, { allowedOrigins: siteText });
    },
    onSuccess: (updated) => {
      const sites = sitesFrom(updated.allowedOrigins);
      setSetup(updated);
      setSiteText(sites);
      setSiteSaved(sites);
      invalidate();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const closeInstall = () => {
    if (siteDirty && can.admin) setDiscard(true);
    else {
      setSetup(null);
      setInstallSecret(null);
    }
  };
  const apps = (keys.data ?? []).filter((app) => !app.deleting);
  const appName = new Map(apps.map((app) => [app.id, app.name]));
  const chosenApp = apps.length === 1 ? apps[0]!.id : keyApp;
  const nameErr = keyName.trim().length > 80 ? "Use 80 characters or fewer." : keyErrors.name;
  const keyReady = keyName.trim() !== "" && chosenApp !== "" && !nameErr;
  const rows = (browserKeys.data ?? []).map((k) => ({ ...k, app: appName.get(k.projectId) ?? "" }));

  const revoke = useMutation({
    mutationFn: (k: BrowserKey) => api.projects.revokeBrowserKey(k.projectId, k.id),
    onSuccess: () => {
      setRevoking(null);
      invalidate();
      toast.push("info", "Browser key revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="SDK keys" />
      <LimitsBanner orgId={orgId} limits={limits} />
      <p className="muted small be-keys-intro">Each app&apos;s settings, including what it keeps, apply to all of its browser and backend keys.</p>
      <section className="card section" aria-labelledby="browser-keys">
        <h2 id="browser-keys" className="be-h2">Browser keys</h2>
        <div className="be-keys-head">
          {can.admin && (
            <div className="be-keys-actions">
              <button type="button" className="btn primary" onClick={() => { setKeyApp(""); setKeyName(""); setKeyErrors({}); setKeyOpen(true); }} disabled={apps.length === 0}>New browser key</button>
            </div>
          )}
        </div>
        {keys.isError || browserKeys.isError ? (
          <ErrorState error={(keys.error ?? browserKeys.error) as Error} retry={() => { void keys.refetch(); void browserKeys.refetch(); }} />
        ) : (
          <div className="stack-table be-table">
            <DataTable<BrowserKey & { app: string }>
              ariaLabel="Browser keys"
              rows={rows}
              rowKey={(k) => k.id}
              loading={keys.isPending || browserKeys.isPending}
              empty={apps.length === 0 ? <>Add an app on <Link to="/orgs/$org/apps" params={{ org: orgId }}>Apps</Link> first. Each browser key belongs to one app.</> : "No browser keys yet."}
              columns={[
                { key: "name", header: "Name", label: "Name", render: (k) => (<><strong>{k.name || "Browser key"}</strong><div className="muted small mono">{k.prefix}…</div></>) },
                { key: "app", header: "App", label: "App", render: (k) => k.app },
                {
                  key: "recording",
                  header: "Recording",
                  label: "Recording",
                  render: (k) => {
                    const app = apps.find((a) => a.id === k.projectId);
                    if (!app) return <span className="muted">—</span>;
                    const rec = recordingInstruction(app.recording, limits);
                    return (
                      <span className={`badge ${rec.tone}`} title={rec.narrowedBy ? `The saved setting is narrowed: ${rec.narrowedBy}. Open billing to change that.` : undefined}>
                        {rec.label}
                      </span>
                    );
                  },
                },
                { key: "used", header: "Last used", label: "Last used", render: (k) => (k.lastUsedAt ? formatRelative(k.lastUsedAt) : <span className="muted">Never</span>) },
                {
                  key: "actions",
                  header: "",
                  className: "row-actions",
                  width: 44,
                  align: "right",
                  render: (k) => {
                    const app = apps.find((a) => a.id === k.projectId);
                    return (
                      <RowMenu label={`Actions for ${k.name || k.prefix}`}>
                        {app && <button type="button" role="menuitem" onClick={() => openInstall(app)}>Install</button>}
                        {app && <button type="button" role="menuitem" onClick={() => openRecording(app)}>Settings</button>}
                        {can.admin && <button type="button" role="menuitem" className="danger" onClick={() => setRevoking(k)}>Revoke key</button>}
                      </RowMenu>
                    );
                  },
                },
              ]}
            />
          </div>
        )}
      </section>

      <section className="card section" aria-labelledby="backend-keys" style={{ marginTop: 16 }}>
        <h2 id="backend-keys" className="be-h2">Backend keys</h2>
        <OrgBackendKeys projects={(keys.data ?? []).map((k) => ({ id: k.id, name: k.name }))} />
      </section>

      <Dialog
        open={!!editing && !!draft}
        onClose={askClose}
        side
        className="project-settings"
        title={editing ? `Settings for ${editing.name}` : "Settings"}
        footer={
          can.admin ? (
            <>
              <button className="btn" onClick={askClose}>Cancel</button>
              <BusyButton className="btn primary" busy={saveRecording.isPending} busyLabel="Saving…" disabled={!dirty || !!sampleError || !!eventCapError || draftSitePending} onClick={() => saveRecording.mutate()}>
                Save changes
              </BusyButton>
            </>
          ) : (
            <button className="btn" onClick={() => setEditing(null)}>Close</button>
          )
        }
      >
        {draft && (
          <>
            {(() => {
              const now = editing ? recordingInstruction(editing.recording, limits) : undefined;
              if (!now?.narrowedBy) return null;
              return (
                <div className="banner warn" role="status" style={{ marginBottom: 12 }}>
                  Pages with this key are told: {now.label}. The setting below is kept and applies again when that changes.
                  {can.billing && (
                    <>
                      {" "}
                      <Link to="/orgs/$org/billing" params={{ org: orgId }}>Open billing</Link>
                    </>
                  )}
                </div>
              );
            })()}
            <Field label="What to record">
              {(p) => (
                <select {...p} className="input" value={draft.mode} disabled={!can.admin} onChange={(e) => setDraft({ ...draft, mode: e.target.value as RecordMode })}>
                  <option value="always">Every visit</option>
                  <option value="conditional">A sample, and when something goes wrong</option>
                  <option value="triggers-only">Only when something goes wrong</option>
                  <option value="off">Do not record</option>
                </select>
              )}
            </Field>
            {draft.mode === "conditional" && (
              <Field label="Share of visits to record" hint="100 records every visit. A lower number records that share, and still records when something goes wrong." error={sampleError}>
                {(p) => (
                  <input {...p} className="input" inputMode="numeric" value={draft.sample} disabled={!can.admin} onChange={(e) => setDraft({ ...draft, sample: e.target.value })} />
                )}
              </Field>
            )}
            <Field
              label="Masking"
              hint={
                draft.privacy === "strict"
                  ? "All page text and typed values are hidden."
                  : draft.privacy === "balanced"
                    ? "Page text is shown, but typed values are hidden."
                    : "Page text and typed values are shown."
              }
            >
              {(p) => (
                <select {...p} className="input" value={draft.privacy} disabled={!can.admin} onChange={(e) => setDraft({ ...draft, privacy: e.target.value as PrivacyMode })}>
                  <option value="strict">All text and inputs</option>
                  <option value="balanced">Inputs only</option>
                  <option value="permissive">Marked and sensitive data only</option>
                </select>
              )}
            </Field>
            <p className="muted small">Passwords, payment details, one-time codes, and anything marked to hide stay hidden in every mode.</p>
            <Field label="Days to keep a session" hint={`This plan keeps a session for up to ${retentionLabel(keepDays)}. Sessions already stored move to the window you pick, and anything older is deleted.`}>
              {(p) => (
                <select {...p} className="input" value={draft.days} disabled={!can.admin} onChange={(e) => setDraft({ ...draft, days: e.target.value })}>
                  {retentionOptions(keepDays, days).map((choice) => (
                    <option key={choice} value={choice}>{retentionLabel(choice)}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Custom events each month" hint="Leave blank to follow the workspace. A number stops custom events for this key once the month reaches it. Recording continues." error={eventCapError}>
              {(p) => (
                <input {...p} className="input" inputMode="numeric" placeholder="Same as the workspace" value={draft.eventCap} disabled={!can.admin} onChange={(e) => setDraft({ ...draft, eventCap: e.target.value.replace(/\D/g, "") })} />
              )}
            </Field>
            <SiteList sites={draft.sites} disabled={!can.admin} onPending={setDraftSitePending} onChange={(sites) => setDraft({ ...draft, sites })} />
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={discard}
        title="Discard changes"
        confirmLabel="Discard"
        onClose={() => setDiscard(false)}
        onConfirm={() => {
          setDiscard(false);
          if (setup && siteDirty) { setSetup(null); setInstallSecret(null); }
          else setEditing(null);
        }}
      >
        <p style={{ margin: 0 }}>{setup && siteDirty ? "Unsaved site changes are discarded." : "Unsaved recording changes are discarded."}</p>
      </ConfirmDialog>

      <Dialog
        open={keyOpen}
        onClose={() => { if (!createKey.isPending) setKeyOpen(false); }}
        title="New browser key"
        width={480}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setKeyOpen(false)} disabled={createKey.isPending}>Cancel</button>
            <BusyButton className="btn primary" busy={createKey.isPending} busyLabel="Creating…" disabled={!keyReady} onClick={() => createKey.mutate()}>Create key</BusyButton>
          </>
        }
      >
        {apps.length > 1 && (
          <Field label="App">
            {(p) => (
              <select {...p} className="input" value={keyApp} onChange={(e) => setKeyApp(e.target.value)}>
                <option value="">Choose an app</option>
                {apps.map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
              </select>
            )}
          </Field>
        )}
        <Field label="Name" error={nameErr} hint="Which site or team uses it.">
          {(p) => <input {...p} className="input" value={keyName} onChange={(e) => { setKeyName(e.target.value); setKeyErrors({}); }} placeholder="Marketing site" autoFocus />}
        </Field>
      </Dialog>

      <Dialog
        open={!!created}
        onClose={() => setCreated(null)}
        title="Browser key created"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCreated(null)}>Done</button>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                const app = apps.find((a) => a.id === created?.key.projectId);
                const secret = created?.secret;
                setCreated(null);
                if (app) openInstall(app, secret);
              }}
            >
              Add it to a site
            </button>
          </>
        }
      >
        <p className="muted">Copy it now. You can't see the full key again after you close this.</p>
        <div className="mono-box" data-testid="browser-key-secret">{created?.secret}</div>
        <div style={{ marginTop: 8 }}>
          <CopyButton value={created?.secret ?? ""} label="Copy key" />
        </div>
      </Dialog>

      <Dialog
        open={!!setup}
        onClose={closeInstall}
        side
        className="project-settings"
        title={setup ? `Add ${setup.name} to your site` : "Install"}
        footer={
          <>
            <button className="btn" onClick={closeInstall}>Done</button>
            {can.admin && (
              <BusyButton className="btn primary" busy={saveSites.isPending} busyLabel="Saving…" disabled={!siteDirty || setupSitePending} onClick={() => saveSites.mutate()}>
                Save sites
              </BusyButton>
            )}
          </>
        }
      >
        {setup && (
          <>
            <Field label="How this site is built">
              {(p) => (
                <select {...p} className="input" value={framework} onChange={(e) => setFramework(e.target.value)}>
                  {FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              )}
            </Field>
            <div className="install-snippet-row">
              <pre className="install-snippet"><code>{installSnippet(framework, installSecret ?? KEY_PLACEHOLDER)}</code></pre>
              <CopyButton value={installSnippet(framework, installSecret ?? KEY_PLACEHOLDER)} />
            </div>
            <SiteList sites={siteText} disabled={!can.admin} onPending={setSetupSitePending} onChange={setSiteText} />
            <div className="install-status" role="status">
              {watch.data && !watch.data.keyOk ? (
                <p>This key is not recognized. Create a new one and use the snippet above.</p>
              ) : refusalIsCurrent(watch.data) && watch.data?.notice ? (
                <p>{noticeText(watch.data.notice)}</p>
              ) : watch.data?.check && watch.data.check !== "empty_visit" ? (
                <p>{checkText(watch.data.check)}</p>
              ) : watch.data?.session ? (
                <>
                  <p>
                    We see a visit{watch.data.session.url ? ` to ${watch.data.session.title || watch.data.session.url}` : ""}.{" "}
                    <Link to="/sessions/$id" params={{ id: watch.data.session.id }}>Open the replay</Link>
                  </p>
                  {watch.data.check === "empty_visit" ? <p>{checkText("empty_visit")}</p> : null}
                  {watch.data.signal ? (
                    <p>
                      First Signal: {watch.data.signal.title}.{" "}
                      <Link to="/issues/$id" params={{ id: watch.data.signal.id }}>Open it</Link>
                    </p>
                  ) : (
                    <p>Watching for the first Signal from this visit.</p>
                  )}
                </>
              ) : (
                <p>Waiting for the first visit. Open the site after the snippet is on the page. This usually takes under a minute.</p>
              )}
            </div>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke browser key"
        confirmLabel="Revoke key"
        busy={revoke.isPending}
        onClose={() => { if (!revoke.isPending) setRevoking(null); }}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      >
        <p style={{ margin: 0 }}>
          <strong>{revoking?.name || revoking?.prefix}</strong> stops working right away. Pages using it stop recording. Sessions already recorded stay readable until their retention expires.
        </p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
